/**
 * Error logs page helpers.
 *
 * @version 2026-04-22-v1
 */
import type { ErrorLogRow, ErrorSeverity } from '../../../lib/types';

export function severityTone(severity: ErrorSeverity) {
    return severity === 'fatal' || severity === 'error'
        ? 'danger'
        : severity === 'warn'
          ? 'warning'
          : 'info';
}

export function parseLogTs(value: string) {
    const normalized = value.trim();
    if (!normalized) return 0;

    const normalizedIso = normalized.includes('T')
        ? /(?:Z|[+-]\d{2}:?\d{2})$/.test(normalized)
            ? normalized
            : `${normalized}Z`
        : `${normalized.replace(' ', 'T')}:00.000Z`;

    const epoch = Date.parse(normalizedIso);
    return Number.isFinite(epoch) ? epoch : 0;
}

export function sanitizeExportField(value: string) {
    return value.replace(/[\t\r\n]+/g, ' ');
}

export function isRealTrace(row: ErrorLogRow) {
    return row.trace_state === 'real' && !!row.trace_id.trim();
}

export function traceUrlForRow(row: ErrorLogRow) {
    if (!isRealTrace(row)) return null;
    return `https://dash.cloudflare.com/?ray=${encodeURIComponent(row.trace_id.trim())}`;
}

export function traceCopyLabel(row: ErrorLogRow) {
    return isRealTrace(row) ? 'Copy request id' : 'Copy correlation id';
}

export function traceAvailabilityLabel(row: ErrorLogRow) {
    return isRealTrace(row) ? 'Cloudflare trace available' : 'Cloudflare trace unavailable';
}

type PreparedErrorLogRow = ErrorLogRow & {
    tsSort: number;
};

export function getTopCodeStats(rows: PreparedErrorLogRow[]) {
    const map = new Map<string, { code: string; count: number; lastTs: string }>();

    rows.forEach((row) => {
        const current = map.get(row.code);
        if (!current) {
            map.set(row.code, { code: row.code, count: row.count, lastTs: row.ts });
            return;
        }

        map.set(row.code, {
            ...current,
            count: current.count + row.count,
            lastTs: row.ts > current.lastTs ? row.ts : current.lastTs
        });
    });

    return Array.from(map.values())
        .sort((a, b) => b.count - a.count || b.lastTs.localeCompare(a.lastTs))
        .slice(0, 6);
}
